import React, { Fragment, useMemo } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { v4 as uuidv4 } from 'uuid';
import { MARKS } from '@contentful/rich-text-types';

interface Props {
  document?: Record<string, any>;
  options?: Record<string, any>;
}

// * Custom parse function that replaces matched special chars with components
// * Chunks input values (Either string, obj, bool, or array of types) and recursively checks them for special
// * chars that match those listed in the specialChars obj, replaces matched chars with their 'component'
// * ReactHtmlParser() parses html tags on the final string
const parse = (value, expressions, iterator) => {
  let increment = iterator;
  const text = typeof value === 'object' ? value : [value];
  const expr = Object.values(expressions);
  const regxp = expr[increment];
  if (!text.length) return text;

  return text.flatMap((val) => {
    if (!val) return '';
    let string = val;

    string = typeof string !== 'string'
      ? string
      : string
        .split(regxp.regex)
        .flatMap((subText, i) => [
          i > 0 && regxp.component(),
          ReactHtmlParser(subText),
        ]);
    if (increment + 1 < expr.length) {
      increment += 1;
      return parse(string, expressions, increment);
    }
    return string;
  });
};

const RichText = ({ document, options }: Props) => useMemo(() => {
  // If the passed data is null, return nothing
  if (!document) return null;

  const config = typeof options === 'function' ? options() : options;

  // * All Special chars to search for ~ Must have regex exp & component
  const specialChars = {
    br: {
      regex: '\n',
      component: () => <br key={uuidv4()} />,
    },
  };

  // * Custom Parser option for Contentful RichText
  const parser = {
    renderText: (text) => parse(text, specialChars, 0),
  };

  // * Default Marks resolvers
  const defaultConfig = {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text) => <em>{text}</em>,
    },
  };

  // * Components after parsing
  const parsedComponents = renderRichText(document, {
    ...defaultConfig,
    ...config,
    ...parser,
  });

  return <Fragment key={uuidv4()}>{parsedComponents}</Fragment>;
}, [document, options]);

export default RichText;
